export interface UserNameParts {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
}

export interface AvatarNameContext {
  profileName?: string | null;
  roles?: string[];
  student?: {
    first_name?: string | null;
    last_name?: string | null;
    display_name?: string | null;
  } | null;
  teacher?: {
    first_name?: string | null;
    last_name?: string | null;
    display_name?: string | null;
  } | null;
}

function firstChar(value?: string | null): string {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '';
}

/** Build initials from first/last name, with display-name and single-name fallbacks. */
export function getUserInitials(parts: UserNameParts): string | null {
  const first = firstChar(parts.firstName);
  const last = firstChar(parts.lastName);

  if (first && last) return `${first}${last}`;
  if (first) return first;
  if (last) return last;

  const display = (parts.displayName || '').trim();
  if (!display) return null;

  const tokens = display.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return `${firstChar(tokens[0])}${firstChar(tokens[tokens.length - 1])}`;
  }
  return firstChar(tokens[0]) || null;
}

function trimName(value?: string | null): string {
  return (value || '').trim();
}

/** Header/profile label: display name, then first + last, then null for caller fallback. */
export function getUserDisplayName(parts: UserNameParts): string | null {
  const display = trimName(parts.displayName);
  if (display) return display;

  const first = trimName(parts.firstName);
  const last = trimName(parts.lastName);
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;

  return null;
}

/** Parse a single profile name string into first/last parts. */
export function parseProfileName(name?: string | null): UserNameParts {
  const trimmed = (name || '').trim();
  if (!trimmed) return {};
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length === 1) {
    return { firstName: tokens[0] };
  }
  return {
    firstName: tokens[0],
    lastName: tokens.slice(1).join(' '),
  };
}

/** Resolve structured name fields for avatar initials (student → teacher → platform profile). */
export function resolveAvatarNameParts(context: AvatarNameContext): UserNameParts {
  const roles = context.roles ?? [];
  const isStudent = roles.includes('STUDENT');
  const isTeacher = roles.includes('TEACHER');

  if (isStudent && context.student) {
    return {
      firstName: context.student.first_name,
      lastName: context.student.last_name,
      displayName: context.student.display_name,
    };
  }

  if (isTeacher && context.teacher) {
    return {
      firstName: context.teacher.first_name,
      lastName: context.teacher.last_name,
      displayName: context.teacher.display_name,
    };
  }

  return parseProfileName(context.profileName);
}

/** Shared avatar display state: image URL + initials from the same priority rules as the header. */
export function resolveAvatarDisplay(context: AvatarNameContext & { avatarUrl?: string | null }): {
  avatarUrl: string | null;
  initials: string | null;
} {
  const avatarUrl = context.avatarUrl?.trim() || null;
  const initials = getUserInitials(resolveAvatarNameParts(context));
  return { avatarUrl, initials };
}
